import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';

import { useMutation } from '@sorare/core/src/hooks/graphql/useMutation';

import { markCareerStepClaimed } from '#src/components/missions/markCareerStepClaimed.ts';
import { RewardUnlockedStory } from '#src/components/missions/stories/RewardUnlockedStory/index.tsx';

import type {
  claimTaskMutation,
  claimTaskMutationVariables,
  useClaimTask_currentUser,
} from './__generated__/useClaimTask.graphql.ts';

const CURRENT_USER_FRAGMENT = gql`
  fragment useClaimTask_currentUser on CurrentUser {
    slug
    xpBalances: inGameCurrencyBalances(
      sport: $sport
      inGameCurrencies: [
        COMMON_XP
        LIMITED_XP
        RARE_XP
        SUPER_RARE_XP
        UNIQUE_XP
      ]
    ) {
      amount
      currency
      capAlmostReached
    }
    gemsBalances: inGameCurrencyBalances(
      sport: $sport
      inGameCurrencies: [COMMON_GEM]
    ) {
      amount
    }
    cardShardsChests(
      sport: $sport
      rarities: [common, limited, rare, super_rare, unique]
    ) {
      id
      rarity
      cardShardsCount
      newThresholdUnlocked
      cardSet {
        slug
        name
      }
    }
  }
` as TypedDocumentNode<useClaimTask_currentUser>;

const CLAIM_TASK_MUTATION = gql`
  mutation claimTaskMutation(
    $taskId: ID!
    $sport: Sport!
    $signedAlternativeRewardConfigs: String
  ) {
    claimTask(
      input: {
        taskId: $taskId
        signedAlternativeRewardConfigs: $signedAlternativeRewardConfigs
      }
    ) {
      rewards {
        id
        ...RewardUnlockedStory_reward
      }
      task {
        id
        aasmState
        ... on ThresholdsStreakTask {
          id
          currentThresholdStatus {
            state
          }
        }
        parentTask {
          id
          ... on DailyActionTask {
            id
            aasmState
            liveScore
          }
        }
      }
      currentUser {
        slug
        ...useClaimTask_currentUser
      }
      errors {
        message
        code
      }
    }
  }
  ${/* oxlint-disable-next-line sorare/no-detached-fragments */ ''}
  ${RewardUnlockedStory.fragments.anyRewardInterface}
  ${CURRENT_USER_FRAGMENT}
` as TypedDocumentNode<claimTaskMutation, claimTaskMutationVariables>;

export const useClaimTask = () => {
  return useMutation(CLAIM_TASK_MUTATION, {
    showErrorsWithSnackNotification: true,
    // Claiming writes the task's new aasmState to the cache, so every mounted
    // query that reads that task (the mission rail, board widgets, "View all …"
    // dialogs, …) is passed to onQueryUpdated. Refetch those to pick up the new
    // list membership. Only active, actually-affected queries are visited — so,
    // unlike refetchQueries: 'active', unrelated queries (layout, config) are
    // left alone, and there's no list of operation names to maintain.
    onQueryUpdated: async observableQuery => observableQuery.refetch(),
    update: (cache, { data }) =>
      markCareerStepClaimed(cache, data?.claimTask?.task),
  });
};

useClaimTask.fragments = {
  currentUser: CURRENT_USER_FRAGMENT,
};
